import { useState, useRef, useEffect, useCallback } from 'react';
import { UploadSimple } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { FileList, FileDetails } from '@/components/FileComponents';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const FileUpload = () => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const loadFiles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/files`);
      setFiles(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === "development") console.error('Error loading files:', error);
    }
  }, []);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File too large! Maximum size is 50MB');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('analyze', 'true');
      const response = await axios.post(`${API}/files/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('File uploaded & analyzed!');
      if (response.data.ai_analysis) {
        toast.info('AI Analysis ready!', { duration: 5000 });
      }
      loadFiles();
      setSelectedFile(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === "development") console.error('Upload error:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const deleteFile = async (fileId) => {
    try {
      await axios.delete(`${API}/files/${fileId}`);
      toast.success('File deleted');
      loadFiles();
      if (selectedFile?.file_id === fileId) {
        setSelectedFile(null);
      }
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  return (
    <div className="h-full flex flex-col bg-black" data-testid="file-upload-panel">
      <div className="border-b border-border p-6 bg-surface">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <UploadSimple size={32} weight="duotone" className="text-foreground" />
            <div>
              <h2 className="text-2xl font-bold tracking-tight" data-testid="files-header-title">AI File Analyzer</h2>
              <p className="text-sm text-muted-foreground">Upload files and let AI analyze them instantly</p>
            </div>
          </div>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileUpload}
              className="hidden"
              accept=".pdf,.txt,.md,.docx,.png,.jpg,.jpeg,.webp,.mp3,.mp4,.wav"
              data-testid="file-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-white text-black hover:bg-gray-200"
              data-testid="upload-button"
            >
              <UploadSimple size={20} weight="bold" className="mr-2" />
              {uploading ? 'Uploading...' : 'Upload File'}
            </Button>
          </div>
        </div>
        <div className="text-xs text-muted-foreground">
          <span className="font-medium">Supported:</span> PDF, TXT, DOCX, Images (PNG, JPG), Audio (MP3, WAV), Video (MP4) - Max 50MB
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        <FileList files={files} selectedFile={selectedFile} onSelectFile={setSelectedFile} />
        <div className="flex-1 overflow-y-auto p-6" data-testid="file-details">
          <FileDetails selectedFile={selectedFile} onDelete={deleteFile} />
        </div>
      </div>
    </div>
  );
};

export default FileUpload;
